import { useCallback } from 'react';
import { useCampaignEditorContext } from 'sections/onsite-campaigns/pages/CampaignEditor/contexts/CampaignEditorContext';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { DateCantBeInThePastRule } from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Workflow/Rules/SplitTest/DateCantBeInThePastRule';
import { useWorkflowForm } from '../../contexts/CampaignWorkflowFormContext';
import { useSegmentSchedulingSelector } from '../Workflows/DisplayWebContent/state/selectors/useSegmentSchedulingSelector';
import { useCampaignSchedulingSelector } from '../../state/CampaignSettings/selectors/useCampaignSchedulingSelector';

export const startDateFormKey = 'segment-schedule-start';

const validate = (t, startDate: Date) => {
  if (DateCantBeInThePastRule({ date: startDate }).result === 'fail') {
    return {
      passed: false,
      validationMessage: t('onsite.editor.workflow.scheduling.inlineValidationMessages.startDateInThePast'),
    };
  }

  return {
    passed: true,
    validationMessage: undefined,
  };
};

export const useStartDate = (messageId: string) => {
  const { t } = useTranslation('campaigns');

  const { actions, state } = useWorkflowForm();
  const { dispatch } = useCampaignEditorContext();

  const onFocusChange = useCallback(
    ({ focused }) => {
      actions.setTouched(startDateFormKey, true);
      actions.setFocused(startDateFormKey, focused);
    },
    [actions],
  );

  const { startDate, endDate } = useSegmentSchedulingSelector(messageId) ?? {};
  const onDateChange = useCallback(
    (startDate: Moment) =>
      dispatch({
        type: 'SET_SEGMENT_START_DATE',
        payload: { id: messageId, startDate: startDate?.toDate() },
      }),
    [dispatch, messageId],
  );

  const validation = validate(t, startDate);
  const campaignSchedule = useCampaignSchedulingSelector();

  let minDate = moment();
  if (campaignSchedule?.startDate) {
    minDate = moment(campaignSchedule.startDate);
  }

  let maxDate = undefined;
  if (campaignSchedule?.endDate) {
    maxDate = moment(campaignSchedule.endDate);
  }
  if (endDate) {
    maxDate = moment(endDate);
  }

  return {
    startDate: startDate ? moment(startDate) : undefined,
    onDateChange,
    onFocusChange,
    focused: state.focused[startDateFormKey],
    validation,
    minDate,
    maxDate,
  };
};
